import IfGlobalOrg from '@/components/organizations/IfGlobalOrg'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import { OrganizationContextNotice } from '@/components/patterns/info-notice'
import {
  IndexDataTable,
  getIndexPaginationMeta,
  IndexTableClearFiltersButton,
  IndexTableExportButton,
  IndexTablePagination,
  IndexTableSearchBox,
  IndexTableToolbar,
} from '@/components/tables/IndexTable'
import {
  fetchAllIndexTablePages,
  fetchIndexTableQueryPage,
  useIndexTableSpreadsheetExport,
} from '@/components/tables/indexTableExport'
import { useIndexTableSearchParams } from '@/components/tables/useIndexTableSearchParams'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Typography } from '@/components/ui/typography'
import { usePhoneFormatter } from '@/hooks/use-phone-formatter'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import queryClient from '@/lib/queryClient'
import { prefetchIndexTableRouteQuery } from '@/lib/services/index-table-query-options'
import { messageQueries, useMessages } from '@/lib/services/messages/messages.query'
import { cn } from '@/lib/utils'
import {
  createFileRoute,
  Link,
  notFound,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import {
  ArrowDownAZ,
  CalendarDays,
  CalendarClock,
  CheckCircle2,
  Mail,
  MessageCircleHeart,
  MessageSquare,
  Phone,
  SlidersHorizontal,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { z } from 'zod/v4'

const DEFAULT_PAGE_SIZE = 20
const defaultMessageListSearchParams = {
  filter: 'all',
  sort: 'received_desc',
  page: 1,
  page_size: DEFAULT_PAGE_SIZE,
}
const strippedMessageListSearchParams = {
  filter: defaultMessageListSearchParams.filter,
  page: defaultMessageListSearchParams.page,
  page_size: defaultMessageListSearchParams.page_size,
}
const messageFilterValues = ['all', 'unread', 'read', 'events', 'non_events']
const messageSortValues = [
  'received_desc',
  'received_asc',
  'name_asc',
  'name_desc',
  'event_date_asc',
  'event_date_desc',
]

const filterPresets = [
  { value: 'all', label: 'All', icon: SlidersHorizontal },
  { value: 'unread', label: 'Unread', icon: Mail },
  { value: 'read', label: 'Read', icon: CheckCircle2 },
  { value: 'events', label: 'Events', icon: CalendarDays },
  { value: 'non_events', label: 'Not Events', icon: MessageSquare },
]

const sortOptions = [
  { value: 'received_desc', label: 'Received Newest', icon: CalendarClock },
  { value: 'received_asc', label: 'Received Oldest', icon: CalendarClock },
  { value: 'name_asc', label: 'Name A-Z', icon: ArrowDownAZ },
  { value: 'name_desc', label: 'Name Z-A', icon: ArrowDownAZ },
  { value: 'event_date_asc', label: 'Event Soonest', icon: CalendarDays },
  { value: 'event_date_desc', label: 'Event Latest', icon: CalendarDays },
]

const messageListUrlSearchSchema = z.object({
  search: z.string().optional().catch(undefined),
  filter: z.enum(messageFilterValues).catch(defaultMessageListSearchParams.filter),
  sort: z.enum(messageSortValues).catch(defaultMessageListSearchParams.sort),
  page: z.coerce.number().int().positive().catch(defaultMessageListSearchParams.page),
  page_size: z.coerce
    .number()
    .int()
    .positive()
    .max(100)
    .catch(defaultMessageListSearchParams.page_size),
})

const messageInboxSortColumns = {
  from: {
    asc: 'name_asc',
    desc: 'name_desc',
    defaultSort: 'name_asc',
  },
  event: {
    asc: 'event_date_asc',
    desc: 'event_date_desc',
    defaultSort: 'event_date_asc',
  },
  received: {
    asc: 'received_asc',
    desc: 'received_desc',
    defaultSort: 'received_desc',
  },
}

export const Route = createFileRoute('/_authenticated/$orgId/messages/')({
  component: RouteComponent,
  validateSearch: (search) => messageListUrlSearchSchema.parse(search),
  search: {
    middlewares: [stripSearchParams(strippedMessageListSearchParams)],
  },
  loader: async ({ context, params }) => {
    if (!context.ability.can('list', 'messages')) throw notFound()
    void prefetchIndexTableRouteQuery(
      context.queryClient,
      messageQueries.index({
        organizationId: params.orgId,
        filters: {
          filter: defaultMessageListSearchParams.filter,
          sort: defaultMessageListSearchParams.sort,
        },
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE,
      })
    )
  },
})

function getMessagesFromResponse(response) {
  return Array.isArray(response) ? response : (response?.data ?? [])
}

function getMessageListSearchParams(search = {}) {
  return {
    ...defaultMessageListSearchParams,
    ...search,
  }
}

function messageCountText(total) {
  return `${total} message${total === 1 ? '' : 's'}`
}

function formatMessagePreview(message) {
  const value = message?.message ?? ''

  return value.length > 1000 ? `${value.slice(0, 1000)}...` : value
}

function formatEventDate(message) {
  if (!message.is_event) {
    return 'Not an event'
  }

  return message.event_date ? dayjs(message.event_date).format('MM/DD/YYYY') : 'Not set'
}

function MessageFilterSelect({ value, onValueChange }) {
  const isFiltered = value !== 'all'

  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label="Filter messages" selected={isFiltered} className="w-fit">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {filterPresets.map((preset) => {
          const Icon = preset.icon

          return (
            <SelectItem key={preset.value} value={preset.value}>
              <Icon className="size-4" />
              {preset.label}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

function MessageSortSelect({ value, onValueChange }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger aria-label="Sort messages" className="w-fit">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {sortOptions.map((option) => {
          const Icon = option.icon

          return (
            <SelectItem key={option.value} value={option.value}>
              <Icon className="size-4" />
              {option.label}
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
  )
}

function getMessageInboxColumns(formatPhone) {
  return [
    {
      id: 'from',
      header: 'From',
      cell: ({ row }) => {
        const message = row.original
        const isRead = Boolean(message.read_at)
        const primaryTextClass = isRead
          ? 'font-normal text-muted-foreground'
          : 'font-semibold text-foreground'

        return (
          <>
            <div className="flex min-w-0 items-center gap-2">
              {!isRead ? <span className="size-2 shrink-0 rounded-full bg-primary" /> : null}
              <span
                data-read-state={isRead ? 'read' : 'unread'}
                className={cn('block truncate', primaryTextClass)}
              >
                {message.name}
              </span>
            </div>
            {message.email ? (
              <Typography variant="caption" className="mt-1 block truncate">
                {message.email}
              </Typography>
            ) : null}
          </>
        )
      },
      meta: {
        label: 'From',
        kind: 'primary',
        icon: ArrowDownAZ,
        sortable: messageInboxSortColumns.from,
        minWidth: '10rem',
        preferredWidth: '13rem',
        exportColumns: [
          {
            label: 'Name',
            value: (message) => message.name,
          },
          {
            label: 'Email',
            value: (message) => message.email || '',
          },
        ],
      },
    },
    {
      id: 'message',
      header: 'Message',
      cell: ({ row }) => {
        const message = row.original
        const isRead = Boolean(message.read_at)

        return (
          <span
            className={cn('block truncate', isRead ? 'text-muted-foreground' : 'text-foreground')}
          >
            {formatMessagePreview(message)}
          </span>
        )
      },
      meta: {
        label: 'Message',
        kind: 'primary',
        icon: MessageSquare,
        minWidth: '14rem',
        preferredWidth: '22rem',
        contentClassName: 'lg:w-full',
        exportValue: (message) => message.message ?? '',
      },
    },
    {
      id: 'phone',
      header: 'Phone',
      cell: ({ row }) => (
        <span className="flex min-w-0 items-center gap-2 text-muted-foreground">
          <Phone className="size-4 shrink-0 text-muted-foreground" />
          <span className="truncate">{formatPhone(row.original.phone)}</span>
        </span>
      ),
      meta: {
        label: 'Phone',
        kind: 'text',
        icon: Phone,
        align: 'center',
        minWidth: '8rem',
        preferredWidth: '9.5rem',
        exportValue: (message) => (message.phone ? formatPhone(message.phone) : ''),
      },
    },
    {
      id: 'event',
      header: 'Event',
      cell: ({ row }) => (
        <span className="block truncate tabular-nums">{formatEventDate(row.original)}</span>
      ),
      meta: {
        label: 'Event',
        exportLabel: 'Event Date',
        kind: 'date',
        icon: CalendarDays,
        sortable: messageInboxSortColumns.event,
        align: 'center',
        preferredWidth: '8.5rem',
        exportValue: (message) => (message.is_event ? (message.event_date ?? '') : ''),
        exportType: 'date',
      },
    },
    {
      id: 'received',
      header: 'Received',
      cell: ({ row }) => {
        const message = row.original
        const isRead = Boolean(message.read_at)
        const primaryTextClass = isRead
          ? 'font-normal text-muted-foreground'
          : 'font-semibold text-foreground'

        return (
          <span
            data-read-state={isRead ? 'read' : 'unread'}
            className={cn('whitespace-nowrap tabular-nums', primaryTextClass)}
          >
            {dayjs(message.created_at).format('MM/DD/YYYY')}
          </span>
        )
      },
      meta: {
        label: 'Received',
        kind: 'date',
        icon: CalendarClock,
        sortable: messageInboxSortColumns.received,
        align: 'center',
        preferredWidth: '8.5rem',
        exportValue: (message) => message.created_at,
        exportType: 'date',
      },
    },
  ]
}

function getMessageInboxRowPresentation(message) {
  return { attention: message.read_at ? undefined : 'unread' }
}

export function RouteComponent() {
  const { orgId } = Route.useParams()
  const path = usePathHelper()
  const navigate = useNavigate()
  const { formatPhone } = usePhoneFormatter()
  const urlSearch = getMessageListSearchParams(Route.useSearch())
  const urlSearchTerm = urlSearch.search ?? ''
  const { updatePage, updatePageSize, updateSearchTerm, updateSort, updateTableParams } =
    useIndexTableSearchParams({
      navigate,
      getSearchParams: getMessageListSearchParams,
      searchTerm: urlSearchTerm,
    })

  const [showGlobalAlert, setShowGlobalAlert] = useState(true)
  const messageFilters = {
    search: urlSearchTerm.trim() || undefined,
    filter: urlSearch.filter,
    sort: urlSearch.sort,
  }
  const hasActiveSearch = urlSearchTerm.trim().length > 0
  const {
    data: messagesResponse,
    isFetching,
    isPending,
  } = useMessages({
    organizationId: orgId,
    filters: messageFilters,
    page: urlSearch.page,
    pageSize: urlSearch.page_size,
  })
  const messages = getMessagesFromResponse(messagesResponse)
  const paginationMeta = getIndexPaginationMeta(
    messagesResponse,
    urlSearch.page,
    urlSearch.page_size
  )
  const columns = useMemo(() => getMessageInboxColumns(formatPhone), [formatPhone])
  const { exportDisabled, exportPending, handleExport } = useIndexTableSpreadsheetExport({
    columns,
    rowCount: paginationMeta.total,
    filename: 'messages.xlsx',
    sheetName: 'Messages',
    loadRows: ({ signal } = {}) =>
      fetchAllIndexTablePages({
        signal,
        fetchPage: ({ page, pageSize, signal }) =>
          fetchIndexTableQueryPage(
            messageQueries.index({
              organizationId: orgId,
              filters: messageFilters,
              page,
              pageSize,
            }),
            { signal }
          ),
      }),
  })

  const handleFilterChange = (value) => updateTableParams({ filter: value })

  const clearFilters = () => updateTableParams({ search: undefined, filter: 'all' })

  const handlePageIntent = (page) => {
    void queryClient.prefetchQuery(
      messageQueries.index({
        organizationId: orgId,
        filters: messageFilters,
        page,
        pageSize: urlSearch.page_size,
      })
    )
  }

  return (
    <div className="space-y-4">
      <IfGlobalOrg>
        {showGlobalAlert ? (
          <OrganizationContextNotice className="mb-4" onDismiss={() => setShowGlobalAlert(false)}>
            Messages are florist-specific.
          </OrganizationContextNotice>
        ) : null}
      </IfGlobalOrg>
      <AdminPageHeader
        icon={MessageCircleHeart}
        title="Messages"
        actions={
          <Can I="create" a="messages">
            <CreateActionButton asChild>
              <Link {...path.messages.create().link}>Add Inquiry</Link>
            </CreateActionButton>
          </Can>
        }
      />

      <div className="space-y-4">
        <IndexDataTable
          data={messages}
          columns={columns}
          sort={urlSearch.sort}
          onSortChange={updateSort}
          rowLink={(message) => path.messages.show(message.id).link}
          getRowPresentation={getMessageInboxRowPresentation}
          getRowId={(message) => String(message.id)}
          isLoading={isPending}
          toolbar={
            <IndexTableToolbar
              primary={
                <IndexTableSearchBox
                  value={urlSearchTerm}
                  onCommit={updateSearchTerm}
                  countText={messageCountText(paginationMeta.total)}
                  isFetching={isFetching}
                  placeholder="Search messages..."
                />
              }
              secondary={
                <>
                  {hasActiveSearch || urlSearch.filter !== 'all' ? (
                    <IndexTableClearFiltersButton onClick={clearFilters} />
                  ) : null}
                  <MessageFilterSelect
                    value={urlSearch.filter}
                    onValueChange={handleFilterChange}
                  />
                  <div className="lg:hidden">
                    <MessageSortSelect value={urlSearch.sort} onValueChange={updateSort} />
                  </div>
                </>
              }
              actions={
                <IndexTableExportButton
                  disabled={exportDisabled}
                  isPending={exportPending}
                  onClick={handleExport}
                />
              }
            />
          }
          emptyState={<Typography variant="supporting">No messages found.</Typography>}
          tableClassName="text-sm lg:min-w-[56rem] lg:table-fixed"
        />

        <IndexTablePagination
          meta={paginationMeta}
          pageSize={urlSearch.page_size}
          onPageChange={updatePage}
          onPageIntent={handlePageIntent}
          onPageSizeChange={updatePageSize}
        />
      </div>
    </div>
  )
}
